import { zodResolver } from '@hookform/resolvers/zod'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import useTranslate from '../../../../lib/hooks/use-translate'
import { GenderEnum } from '../../../../lib/types/enums/common/gender.enum'
import { ContactEnum } from '../../../../lib/types/enums/enrollment/contact.enum'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form } from '../../../elements/forms/form'
import ValidationButtons from '../../../elements/validation-buttons'
import ParentsInformationEnrollmentForm from '../../enrollments/enrollment-form/parents-information-enrollment-form'

export type ParentInformationInput = Pick<
  EnrollmentInput,
  | 'parent1Gender'
  | 'parent1FirstName'
  | 'parent1LastName'
  | 'parent1Email'
  | 'parent1Job'
  | 'parent1PhoneNumber'
  | 'parent1PhoneNumber2'
  | 'parent2Gender'
  | 'parent2FirstName'
  | 'parent2LastName'
  | 'parent2Email'
  | 'parent2Job'
  | 'parent2PhoneNumber'
  | 'parent2PhoneNumber2'
  | 'contactPriority'
  | 'isAA'
  | 'extraOptionLabel'
>

export const PARENT_INFORMATION_BASE_SCHEMA = (t: TFunction<AvailableLanguages, undefined>) =>
  z.object({
    parent1Gender: createEnumSchema({ enumType: GenderEnum, fieldName: 'Genre' }),
    parent1FirstName: createStringSchema('Prénom'),
    parent1LastName: createStringSchema('NOM'),
    parent1Email: createStringSchema('Email', { isEmail: true }),
    parent1Job: createStringSchema('Profession', { nullish: true }),
    parent1PhoneNumber: PHONE_NUMBER_SCHEMA(t),
    parent1PhoneNumber2: PHONE_NUMBER_SCHEMA(t).nullish(),
    parent2Gender: createEnumSchema({
      enumType: GenderEnum,
      fieldName: 'Genre',
      nullish: true
    }),
    parent2FirstName: createStringSchema('Prénom', { nullish: true }),
    parent2LastName: createStringSchema('NOM', { nullish: true }),
    parent2Email: createStringSchema('Email', { nullish: true, isEmail: true }),
    parent2Job: createStringSchema('Profession', { nullish: true }),
    parent2PhoneNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
    parent2PhoneNumber2: PHONE_NUMBER_SCHEMA(t).nullish(),
    isAA: z.boolean().optional(),
    contactPriority: createEnumSchema({
      enumType: ContactEnum,
      fieldName: 'Contact prioritaire'
    }),
    extraOptionLabel: createStringSchema('Numéro Oaasis', { nullish: true })
  })

export const addParentContactPriorityValidation = (
  t: TFunction<AvailableLanguages, undefined>,
  value: z.infer<ReturnType<typeof PARENT_INFORMATION_BASE_SCHEMA>>,
  ctx: z.RefinementCtx
) => {
  const isParent2Filled = [
    value.parent2FirstName,
    value.parent2LastName,
    value.parent2Email,
    value.parent2Job,
    value.parent2PhoneNumber,
    value.parent2PhoneNumber2
  ].some((field) => !!field?.trim())

  if (
    !value.parent2Gender &&
    (isParent2Filled || value.contactPriority === ContactEnum.PARENT_2)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['parent2Gender'],
      message: t('errors.enum.required', { fieldName: 'Genre' })
    })
  }

  if (value.contactPriority !== ContactEnum.PARENT_2) return

  if (!value.parent2FirstName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['parent2FirstName'],
      message: t('errors.string.required', { fieldName: 'Prénom' })
    })
  }

  if (!value.parent2LastName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['parent2LastName'],
      message: t('errors.string.required', { fieldName: 'NOM' })
    })
  }

  if (!value.parent2PhoneNumber?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['parent2PhoneNumber'],
      message: t('errors.phone.required')
    })
  }

  if (!value.parent2Email?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['parent2Email'],
      message: t('errors.string.required', { fieldName: 'Email' })
    })
  }
}

export const PARENT_INFORMATION_SCHEMA = (t: TFunction<AvailableLanguages, undefined>) =>
  PARENT_INFORMATION_BASE_SCHEMA(t).superRefine((value, ctx) =>
    addParentContactPriorityValidation(t, value, ctx)
  )

type Props = BaseFormProps<ParentInformationInput, true> & {
  enrollmentId: number
}

const ParentInformationForm = ({
  defaultValues,
  className,
  enrollmentId,
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()
  const form = useForm<ParentInformationInput>({
    resolver: zodResolver(PARENT_INFORMATION_SCHEMA(t)),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={className}>
          <ParentsInformationEnrollmentForm enrollmentId={enrollmentId} />
        </div>
        <ValidationButtons hasBorderTop {...validationButtonProps} />
      </form>
    </Form>
  )
}

export default ParentInformationForm
